<template>
  <div class="recharge-records">
    <el-tabs v-model="activeTab" @tab-change="handleTabChange">
      <el-tab-pane :label="$t('rechargeRecords.qrCodePay')" name="easyPay"></el-tab-pane>
      <el-tab-pane :label="$t('rechargeRecords.usdt')" name="usdt"></el-tab-pane>
    </el-tabs>

    <div class="filters">
      <div class="filter-group">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          :range-separator="$t('rechargeRecords.to')"
          :start-placeholder="$t('rechargeRecords.startDate')"
          :end-placeholder="$t('rechargeRecords.endDate')"
          value-format="YYYY-MM-DD"
          @change="handleDateChange"
        />

        <el-select
          v-model="rechargeStatus"
          :placeholder="$t('rechargeRecords.rechargeStatus')"
          clearable
          @change="handleFilterChange"
        >
          <el-option
            v-for="status in rechargeStatuses"
            :key="status.value"
            :label="status.label"
            :value="status.value"
          />
        </el-select>
      </div>

      <div class="filter-actions">
        <el-button type="primary" @click="loadRecords">
          {{ $t('common.search') }}
        </el-button>
        <el-button @click="resetFilters">
          {{ $t('common.reset') }}
        </el-button>
      </div>
    </div>

    <el-table
      :data="records"
      v-loading="loading"
      stripe
      style="width: 100%"
    >
      <el-table-column
        prop="orderNo"
        :label="$t('rechargeRecords.orderNo')"
        min-width="150"
      />
      <el-table-column
        prop="amount"
        :label="$t('rechargeRecords.amount')"
        min-width="100"
      >
        <template #default="scope">
          {{ formatAmount(scope.row.amount) }}
        </template>
      </el-table-column>
      <el-table-column
        prop="originalAmount"
        :label="$t('rechargeRecords.originalAmount')"
        min-width="100"
      >
        <template #default="scope">
          {{ formatAmount(scope.row.originalAmount) }}
        </template>
      </el-table-column>
      <el-table-column
        prop="feeAmount"
        :label="$t('rechargeRecords.feeAmount')"
        min-width="100"
      >
        <template #default="scope">
          {{ formatAmount(scope.row.feeAmount) }}
        </template>
      </el-table-column>
      <el-table-column
        prop="rechargeTypeName"
        :label="$t('rechargeRecords.rechargeTypeName')"
        min-width="100"
      />
      <el-table-column
        prop="payType"
        :label="$t('rechargeRecords.payType')"
        min-width="100"
      />
      <el-table-column
        prop="createTime"
        :label="$t('rechargeRecords.createTime')"
        min-width="150"
      >
        <template #default="scope">
          {{ formatDate(scope.row.createTime) }}
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="total > 0"
      v-model:current-page="currentPage"
      v-model:page-size="pageSize"
      :page-sizes="[10, 20, 50, 100]"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
    />
  </div>
</template>

<script>
import {
  getEasyPayRechargeRecords,
  getUsdtRechargeRecords
} from '@/api/recharge'
import { ElMessage } from 'element-plus'

export default {
  name: 'RechargeRecords',
  data() {
    return {
      activeTab: 'easyPay',
      dateRange: null,
      rechargeStatus: null,
      records: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      rechargeStatuses: [
        { value: 1, label: this.$t('rechargeRecords.statuses.success') },
        { value: 0, label: this.$t('rechargeRecords.statuses.pending') }
      ]
    }
  },
  created() {
    this.loadRecords()
  },
  methods: {
    async loadRecords() {
      this.loading = true

      const params = {
        pageNum: this.currentPage,
        pageSize: this.pageSize
      }

      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = this.dateRange[0]
        params.endTime = this.dateRange[1]
      }

      if (this.rechargeStatus !== null) {
        params.rechargeType = this.rechargeStatus
      }

      try {
        let response

        switch (this.activeTab) {
          case 'easyPay':
            response = await getEasyPayRechargeRecords(params)
            break
          case 'usdt':
            response = await getUsdtRechargeRecords(params)
            break
        }

        if (response && response.status === 200) {
          this.records = response.data.list || []
          this.total = response.data.total || 0
        } else {
          ElMessage.error(response?.msg || this.$t('rechargeRecords.loadFailed'))
        }
      } catch (error) {
        console.error('Load recharge records error:', error)
        ElMessage.error(this.$t('rechargeRecords.loadError'))
      } finally {
        this.loading = false
      }
    },
    handleTabChange() {
      this.currentPage = 1
      this.records = []
      this.total = 0
      this.loadRecords()
    },
    handleDateChange() {
      this.currentPage = 1
      this.loadRecords()
    },
    handleFilterChange() {
      this.currentPage = 1
      this.loadRecords()
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.loadRecords()
    },
    handlePageChange(val) {
      this.currentPage = val
      this.loadRecords()
    },
    resetFilters() {
      this.dateRange = null
      this.rechargeStatus = null
      this.currentPage = 1
      this.loadRecords()
    },
    formatAmount(amount) {
      if (amount === null || amount === undefined) {
        return '-'
      }
      return `¥${Number(amount).toFixed(2)}`
    },
    formatDate(dateString) {
      if (!dateString) return '-'

      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }
  }
}
</script>

<style scoped>
.recharge-records {
  width: 100%;
}

.filters {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 20px 0;
}

.filter-group {
  display: flex;
  gap: 12px;
  align-items: center;
}

.filter-actions {
  display: flex;
  gap: 12px;
}

.el-pagination {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

@media (max-width: 768px) {
  .filter-group {
    flex-direction: column;
  }

  .filter-group .el-date-picker,
  .filter-group .el-select {
    width: 100%;
  }

  .filter-actions {
    width: 100%;
  }

  .filter-actions .el-button {
    flex: 1;
  }
}

@media (max-width: 480px) {
  .el-table {
    font-size: 12px;
  }
}
</style>